/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { memo, useState } from "react";
import { useTranslation } from "react-i18next";
import { InfoRow, SectionEmpty } from "./components/ProfileInfo";
import GenericModal from "components/Modal";
import { FiEye } from "react-icons/fi"; // Eye icon for the "View details" action

// Full project detail modal shown when user clicks "View" on a card
function ProjectDetailModal({
  project,
  onClose,
}: {
  project: any;
  onClose: () => void;
}) {
  const { t } = useTranslation();

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <GenericModal
      isOpen={!!project}
      onClose={onClose}
      header={project?.name}
      size="md"
    >
      <div className="divide-y divide-gray-100 dark:divide-gray-700">
        <InfoRow
          label={t("profile.managerText")}
          value={<span className="capitalize">{project?.manager}</span>}
        />
        <InfoRow label={t("profile.teamText")} value={project?.teamSize} />
        <InfoRow
          label="Start Date"
          value={formatDate(project?.startDate)}
        />
        <InfoRow
          label="Completion Date"
          value={formatDate(project?.completionDate)}
        />
        <InfoRow
          label="Client Contact"
          value={project?.clientContactName}
        />
        <InfoRow
          label="Client Number"
          value={project?.clientContactNumber}
        />
        {project?.projectDetail && (
          <div className="px-4 py-3">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
              Details
            </p>
            {/* Full detail text — no truncation */}
            <p className="text-sm text-gray-700 dark:text-gray-300 break-words whitespace-pre-wrap">
              {project.projectDetail}
            </p>
          </div>
        )}
        {project?.comments && (
          <div className="px-4 py-3">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
              Comments
            </p>
            <p className="text-sm text-gray-700 dark:text-gray-300 break-words whitespace-pre-wrap">
              {project.comments}
            </p>
          </div>
        )}
      </div>
    </GenericModal>
  );
}

function UserProjects({ user }: any) {
  const { t } = useTranslation();

  // Track which project is open in the detail modal; null means modal is closed
  const [selectedProject, setSelectedProject] = useState<any>(null);

  const hasProjects =
    user &&
    Array.isArray(user.userProjectWithAssignedDate) &&
    user.userProjectWithAssignedDate.length > 0;

  if (!user) return null;

  return (
    <div className="w-full py-1">
      {hasProjects ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {user.userProjectWithAssignedDate.map((project: any) => (
            <div
              key={project.projectId}
              className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-md transition-shadow"
            >
              {/* Card header — project name + View button */}
              <div className="px-4 py-2.5 bg-gray-50 dark:bg-gray-800/60 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-800 dark:text-gray-200 capitalize truncate">
                  {project.name}
                </span>
                {/* Eye icon button — opens full detail modal for this card */}
                <button
                  onClick={() => setSelectedProject(project)}
                  className="ml-2 flex-shrink-0 p-1 rounded text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                  aria-label={`View full details for ${project.name}`}
                  title="View details"
                >
                  <FiEye size={15} />
                </button>
              </div>

              {/* Card body — 3 summary fields with truncation */}
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                <InfoRow
                  label={t("profile.managerText")}
                  value={<span className="capitalize">{project.manager}</span>}
                />
                <InfoRow label={t("profile.teamText")} value={project.teamSize} />
                {project.projectDetail && (
                  <div className="px-4 py-2.5">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                      Details
                    </p>
                    {/* Truncated to 3 lines on card; full text shown in modal */}
                    <p className="text-sm text-gray-700 dark:text-gray-300 break-words line-clamp-3">
                      {project.projectDetail}
                    </p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <SectionEmpty message="No projects assigned/available" />
      )}

      {/* Full detail modal — rendered once, driven by selectedProject state */}
      {selectedProject && (
        <ProjectDetailModal
          project={selectedProject}
          onClose={() => setSelectedProject(null)}
        />
      )}
    </div>
  );
}

export default memo(UserProjects);
